<template>
    <BaseLink
        :href="data.return_url"
        class="border-b border-[#E8E8E8] p-6 h-32 flex justify-between items-center gap-4 w-full transition-all group hover:bg-[#F7F7F7]"
        :data="{ chat_source: CHATSOURCE.PAST_CONVERSATIONS }">
        <div class="grow text-left">
            <p class="body-small text-valence-grey-750 mb-1 date">{{ convertToDateTime(data.updated_at).toFormat("LLL dd, yyyy") }}</p>
            <p v-if="!isRenaming" class="title-body fs-mask title">{{ truncate(data.title, { length: TRUNCATE_LENGTH, omission: "..." }) }}</p>
            <div v-else class="flex w-full gap-2 items-center" @click.prevent.stop>
                <input
                    ref="renameInput"
                    v-model.trim="newTitle"
                    class="grow body-small focus-visible:outline-hidden rounded-lg p-2 w-full border border-valence-grey-300 fs-mask"
                    :maxlength="120"
                    @keydown.enter.prevent.stop="handleSaveTitle" />
                <button
                    aria-label="Save conversation title"
                    :disabled="!newTitle"
                    class="rounded-full hover:bg-valence-primary-500/10 px-1 flex justify-center items-center disabled:text-valence-grey-750"
                    @click="handleSaveTitle">
                    <i class="bi bi-check text-xl" aria-hidden="true" />
                </button>
                <button aria-label="Cancel rename" class="rounded-full hover:bg-valence-primary-500/10 px-1 flex justify-center items-center" @click="isRenaming = false">
                    <i class="bi bi-x text-xl" aria-hidden="true" />
                </button>
            </div>
        </div>
        <BaseDropdownMenu :sections="menuSections" side="left" align="center" :side-offset="10" @select="handleMenuSelect" @close-auto-focus="handleMenuCloseAutoFocus">
            <template #trigger>
                <button
                    type="button"
                    aria-label="Conversation options"
                    class="rounded-full hover:bg-valence-primary-500/10 aria-expanded:bg-valence-primary-500/10 w-10 h-10 flex justify-center items-center"
                    @click.stop.prevent>
                    <i class="bi bi-three-dots text-valence-grey-600 text-xl/none" aria-hidden="true" />
                </button>
            </template>
        </BaseDropdownMenu>
        <div class="text-valence-primary-500 body-small whitespace-nowrap">
            <span class="hidden md:inline">{{ ctaText }}</span
            ><i class="bi bi-arrow-right icon-bold ml-1.5" aria-hidden="true" />
        </div>
    </BaseLink>
</template>

<script setup>
import { convertToDateTime } from "/js/dateUtils";
import BaseDropdownMenu from "~vue/components/BaseDropdownMenu.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { CHATSOURCE } from "~vue/utils/constants";
import truncate from "lodash/truncate";
import { computed, nextTick, ref } from "vue";

const TRUNCATE_LENGTH = 120;

const { data, deletionDisabled } = defineProps({
    deletionDisabled: { type: Boolean },
    data: { type: Object, required: true },
    ctaText: { type: String, default: "Start Chat" },
});

const emit = defineEmits(["delete", "rename"]);

const menuSections = computed(() => {
    const items = [{ id: "rename", type: "button", label: "Rename", icon: "bi bi-pencil" }];
    // Delete is hidden when the user is on legal/retention hold; rename stays
    // available because the v1 server endpoint only blocks delete.
    if (!deletionDisabled) {
        items.push({ id: "delete", type: "button", label: "Delete", icon: "bi bi-trash" });
    }
    return [{ id: "actions", items }];
});

const handleDeletePastConversation = () => {
    emit("delete", { kind: data.kind, hashid: data.hashid, uuid: data.uuid });
};

const renameInput = ref();
const isRenaming = ref(false);
const newTitle = ref("");

const handleStartRename = () => {
    newTitle.value = data.title ?? "";
    isRenaming.value = true;
};

const handleMenuCloseAutoFocus = async (event) => {
    if (!isRenaming.value) {
        return;
    }

    event.preventDefault();
    await nextTick();
    // Reka removes its modal focus scope in a timer after this event.
    globalThis.setTimeout(() => {
        renameInput.value?.focus();
        renameInput.value?.select();
    }, 0);
};

const handleMenuSelect = (item) => {
    if (item.id === "rename") {
        handleStartRename();
    } else if (item.id === "delete") {
        handleDeletePastConversation();
    }
};

const handleSaveTitle = () => {
    if (!newTitle.value) {
        return;
    }
    isRenaming.value = false;
    emit("rename", { kind: data.kind, hashid: data.hashid, uuid: data.uuid, newTitle: newTitle.value });
};
</script>
